import { ProjectFormFields } from '@/components/ProjectForm/ProjectForm.types';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import {
  DigitalizationProjectStatus,
  ICreateDigitalizationProjectDto,
  ICreateProjectDto,
  IDigitalizationProjectDetailsDto,
  ILocationDto,
  IProject,
  IProjectDetailsDto,
  IUpdateDigitalizationProjectDto,
  IUpdateProjectDto,
  ProjectStatus,
  ProjectType,
  Serialized,
} from 'types';

interface IProjectLocation {
  value: string | null;
  text: string;
}

export class ProjectDataService {
  constructor(private readonly mode: ProjectType) {}

  public static async createProject(
    projectData: ProjectFormFields,
    assignedDocuManagers: string[],
  ): Promise<IProject> {
    const body: ICreateProjectDto =
      ProjectDataService.transformFormFieldsToSupplierProjectData(
        projectData,
        'create',
        assignedDocuManagers,
      );
    const response = await axios.post<IProject>(
      endpointsList.projects.addProject,
      body,
    );
    return response.data;
  }

  public static async createDigitalizationProject(
    projectData: ProjectFormFields,
    docuResponsibles: string[],
  ): Promise<string> {
    const body = await this.transformFormFieldsToDigitalizationProjectData(
      projectData,
      'create',
      docuResponsibles,
    );
    const response = await axios.post<string>(
      endpointsList.digitalizationProjects.createDigitalizationProject,
      body,
    );
    return response.data;
  }

  public static async updateSupplierProject(
    projectId: string,
    projectData: ProjectFormFields,
  ): Promise<IProjectDetailsDto> {
    const body = this.transformFormFieldsToSupplierProjectData(
      projectData,
      'update',
    );
    const response = await axios.patch<IProjectDetailsDto>(
      endpointsList.projects.updateProject(projectId),
      body,
    );
    return response.data;
  }

  public static async updateDigitalizationProject(
    projectId: string,
    projectData: ProjectFormFields,
  ): Promise<void> {
    const body = await this.transformFormFieldsToDigitalizationProjectData(
      projectData,
      'update',
    );
    await axios.put(
      endpointsList.digitalizationProjects.updateDigitalizationProject(
        projectId,
      ),
      body,
    );
  }

  public static async getSupplierProjectData(
    projectId: string,
  ): Promise<IProjectDetailsDto> {
    const response = await axios.get<Serialized<IProjectDetailsDto>>(
      endpointsList.projects.getProjectById(projectId),
    );
    return this.deserializeSupplierProjectData(response.data);
  }

  public static async getDigitalizationProjectData(
    projectId: string,
  ): Promise<IDigitalizationProjectDetailsDto> {
    const response = await axios.get<
      Serialized<IDigitalizationProjectDetailsDto>
    >(
      endpointsList.digitalizationProjects.getDigitalizationProjectSettings(
        projectId,
      ),
    );
    return this.deserializeDigitalizationProjectData(response.data);
  }

  public async getProjectLocation(projectId: string): Promise<string> {
    const endpoint =
      this.mode === ProjectType.SUPPLIER_PROJECT
        ? endpointsList.projects.getProjectLocation(projectId)
        : endpointsList.digitalizationProjects.getDigitalizationProjectLocation(
            projectId,
          );

    const response = await axios.get(endpoint);
    return response.data.toString();
  }

  public static async getPossibleProjectLocationsForSelect(): Promise<
    IProjectLocation[]
  > {
    const response = await axios.get<ILocationDto[]>(
      endpointsList.prodisApiIntegration.locations(),
    );
    const locations: IProjectLocation[] = response.data.map((loc) => ({
      text: `${loc.locationId} - ${loc.name}`,
      value: loc.locationId,
    }));
    return locations.sort((a, b) => {
      const textA = a.text.toUpperCase();
      const textB = b.text.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }

  private static deserializeSupplierProjectData(
    project: Serialized<IProjectDetailsDto>,
  ): IProjectDetailsDto {
    return {
      ...project,
      projectDuration:
        project.projectDuration &&
        project.projectDuration.durationStart &&
        project.projectDuration.durationEnd
          ? {
              durationStart: new Date(project.projectDuration.durationStart),
              durationEnd: new Date(project.projectDuration.durationEnd),
            }
          : undefined,
    };
  }

  private static deserializeDigitalizationProjectData(
    project: Serialized<IDigitalizationProjectDetailsDto>,
  ): IDigitalizationProjectDetailsDto {
    return {
      ...project,
      duration:
        project.duration &&
        project.duration.durationStart &&
        project.duration.durationEnd
          ? {
              durationStart: new Date(project.duration.durationStart),
              durationEnd: new Date(project.duration.durationEnd),
            }
          : undefined,
    };
  }

  public static transformFormFieldsToSupplierProjectData(
    formFields: ProjectFormFields,
    dataType: 'create',
    assignedDocuManagers: string[],
  ): ICreateProjectDto;
  public static transformFormFieldsToSupplierProjectData(
    formFields: ProjectFormFields,
    dataType: 'update',
  ): IUpdateProjectDto;
  public static transformFormFieldsToSupplierProjectData(
    formFields: ProjectFormFields,
    dataType: 'create' | 'update',
    assignedDocuManagers?: string[],
  ): ICreateProjectDto | IUpdateProjectDto {
    const updateData = {
      projectTitle: formFields.title,
      externalProjectId: formFields.externalId,
      projectStatus: formFields.status,
      projectDescription: formFields.description,
      inspectionDueTime: formFields.inspectionDueTime,
      projectDuration: formFields.duration
        ? {
            durationStart: formFields.duration[0],
            durationEnd: formFields.duration[1],
          }
        : undefined,
    } satisfies IUpdateProjectDto;
    if (dataType === 'update') {
      return updateData;
    } else if (dataType === 'create') {
      return {
        ...updateData,
        projectNumber: formFields.number,
        projectLocation: formFields.location,
        assignedDocuManagers: assignedDocuManagers!,
      } satisfies ICreateProjectDto;
    } else throw new Error('Invalid data type');
  }

  public static transformSupplierProjectDataToFormFields(
    projectData: IProjectDetailsDto,
  ): ProjectFormFields {
    return {
      title: projectData.projectTitle,
      number: projectData.projectNumber,
      externalId: projectData.externalProjectId,
      location: projectData.projectLocation,
      status: projectData.projectStatus,
      description: projectData.projectDescription,
      inspectionDueTime: projectData.inspectionDueTime,
      duration: projectData.projectDuration
        ? [
            projectData.projectDuration.durationStart,
            projectData.projectDuration.durationEnd,
          ]
        : undefined,
    };
  }

  public static async transformFormFieldsToDigitalizationProjectData(
    formFields: ProjectFormFields,
    dataType: 'create',
    docuResponsibles: string[],
  ): Promise<ICreateDigitalizationProjectDto>;
  public static async transformFormFieldsToDigitalizationProjectData(
    formFields: ProjectFormFields,
    dataType: 'update',
  ): Promise<IUpdateDigitalizationProjectDto>;
  public static async transformFormFieldsToDigitalizationProjectData(
    formFields: ProjectFormFields,
    dataType: 'create' | 'update',
    docuResponsibles?: string[],
  ): Promise<
    ICreateDigitalizationProjectDto | IUpdateDigitalizationProjectDto
  > {
    const updateData = {
      ...formFields,
      site: formFields.location,
      // TODO: Merge enums
      status: formFields.status as unknown as DigitalizationProjectStatus,
      duration: formFields.duration
        ? {
            durationStart: formFields.duration[0],
            durationEnd: formFields.duration[1],
          }
        : undefined,
    };

    if (dataType === 'update') {
      return updateData satisfies IUpdateDigitalizationProjectDto;
    } else if (dataType === 'create') {
      return {
        ...updateData,
        docuResponsibles: docuResponsibles!,
      } satisfies ICreateDigitalizationProjectDto;
    } else throw new Error('Invalid data type');
  }

  public static transformDigitalizationProjectDataToFormFields(
    projectData: IDigitalizationProjectDetailsDto,
  ): ProjectFormFields {
    return {
      title: projectData.title,
      number: projectData.number,
      location: projectData.site,
      // TODO: Merge enums
      status: projectData.status as unknown as ProjectStatus,
      description: projectData.description,
      duration: projectData.duration
        ? [projectData.duration.durationStart, projectData.duration.durationEnd]
        : undefined,
      // TODO: Fix after adding conditional `ProjectFormFields` type
      inspectionDueTime: 0,
    };
  }

  public static async getSupplierProjectTitleById(
    projectId: string,
  ): Promise<string> {
    const response = await axios.get<string>(
      endpointsList.projects.getProjectNameById(projectId),
    );
    return response.data;
  }

  public static async getDigitalizationProjectTitleById(
    projectId: string,
  ): Promise<string> {
    const response = await axios.get<string>(
      endpointsList.digitalizationProjects.getDigitalizationProjectTitle(
        projectId,
      ),
    );
    return response.data;
  }

  public static async getProjectIdByExternalId(
    extenalProjectId: string,
  ): Promise<string> {
    const response = await axios.get<string>(
      endpointsList.projects.getProjectIdByExternalId(extenalProjectId),
    );
    return response.data;
  }
}
